/**
 * A montagem do documento do Dossiê, regra PURA: recebe as linhas lidas e devolve o HTML que vira
 * o `.doc` (`montarHtmlDossie` e as três funções de seção, `admin.html:5447-5557`; o desenho do
 * radar, `construirSvgRadar`, `4935-4976`; linha 213 do inventário).
 *
 * O TEXTO SAI BYTE A BYTE IGUAL AO DO LEGADO, inclusive os espaços e as quebras de linha do corpo:
 * é um documento que a Lu entrega, e uma diferença aqui aparece no arquivo da cliente.
 *
 * O LEGADO NÃO ESCAPA as respostas ao montar o HTML (`${valorTexto}`, `5455`), e a migração repete:
 * o documento é o mesmo, com o mesmo comportamento para resposta que contenha `<`.
 */

// Os rótulos das seções, na cópia única da ficha (frente A2).
import {
  ETAPAS_COLORACAO_CHECKBOX,
  FAMILIAS_CORES_COLORACAO,
  ITENS_MILHOES,
  PERGUNTAS_ATENDIMENTO,
  PERGUNTAS_AVALIATIVO,
  PERGUNTAS_GERAL,
  PERGUNTAS_PERSONALIDADE,
} from '../../constantes.js';
// A geometria do radar, com as contas do legado (frente A2).
import { geometriaDoRadar, type PontoRaiz } from '../../comum-ficha/regras.js';

/** Uma linha lida por nome de coluna, como o legado lê `dados[coluna]`. */
export type LinhaLida = Readonly<Record<string, unknown>> | null;

/**
 * As respostas de um questionário, na ordem do mapa (`linhasRespostasHtml`, `admin.html:5447-5458`):
 * vazio, nulo e lista vazia não entram; lista vira texto separado por vírgula.
 */
export function linhasRespostasHtml(dados: LinhaLida, mapaPerguntas: Readonly<Record<string, string>>): string {
  if (!dados) return '<p><em>Cliente ainda não respondeu esse questionário.</em></p>';
  let linhas = '';
  for (const coluna of Object.keys(mapaPerguntas)) {
    const valor = dados[coluna];
    // Nulo, ausente e texto vazio não viram linha (`5452`).
    if (valor === null || valor === undefined || valor === '') continue;
    // Lista vazia também não (`5453`).
    if (Array.isArray(valor) && valor.length === 0) continue;
    const valorTexto = Array.isArray(valor) ? valor.join(', ') : String(valor);
    linhas += `<p style="margin:0 0 8px;"><strong>${mapaPerguntas[coluna]}:</strong> ${valorTexto}</p>`;
  }
  return linhas || '<p><em>Cliente respondeu, mas todos os campos vieram vazios.</em></p>';
}

/** Os itens marcados do Checklist de Milhões (`linhasMilhoesHtml`, `admin.html:5460-5465`). */
export function linhasMilhoesHtml(dados: LinhaLida): string {
  if (!dados) return '<p><em>Checklist de milhões ainda não preenchido.</em></p>';
  // Marcado é a coluna verdadeira, como o `dados[item.col]` do legado.
  const marcados = ITENS_MILHOES.filter((item) => Boolean(dados[item.col]));
  if (marcados.length === 0) return '<p><em>Nenhum item marcado.</em></p>';
  return '<ul>' + marcados.map((item) => `<li><strong>${item.elemento}:</strong> ${item.efeito}</li>`).join('') + '</ul>';
}

// Uma lista de texto de uma coluna, com o `|| []` do legado para coluna ausente.
function listaDe(valor: unknown): readonly string[] {
  return Array.isArray(valor) ? (valor as readonly string[]) : [];
}

// Um texto de uma coluna, para as características e a estação.
function textoDe(valor: unknown): string {
  return typeof valor === 'string' ? valor : valor == null ? '' : String(valor);
}

/** A ficha de Coloração (`linhasColoracaoHtml`, `admin.html:5467-5495`). */
export function linhasColoracaoHtml(dados: LinhaLida): string {
  if (!dados) return '<p><em>Ficha de coloração ainda não preenchida.</em></p>';
  let linhas = '';
  // Etapas 1 a 5: as opções marcadas de cada uma.
  for (const etapa of ETAPAS_COLORACAO_CHECKBOX) {
    const valores = listaDe(dados[etapa.campo]);
    if (valores.length > 0) linhas += `<p style="margin:0 0 8px;"><strong>${etapa.titulo}:</strong> ${valores.join(', ')}</p>`;
  }
  // Etapa 6: as quatro características preenchidas, numa linha só.
  const caracteristicas = (
    [
      ['Contraste', textoDe(dados.caracteristica_contraste)],
      ['Intensidade', textoDe(dados.caracteristica_intensidade)],
      ['Temperatura', textoDe(dados.caracteristica_temperatura)],
      ['Profundidade', textoDe(dados.caracteristica_profundidade)],
    ] as const
  ).filter(([, v]) => v);
  if (caracteristicas.length > 0) {
    linhas += `<p style="margin:0 0 8px;"><strong>Etapa 6 | Características:</strong> ${caracteristicas.map(([l, v]) => `${l}: ${v}`).join(' · ')}</p>`;
  }
  // Etapa 7: os comparativos com pelo menos um lado preenchido.
  const comparativos = (Array.isArray(dados.comparativo_estacoes) ? dados.comparativo_estacoes : []).filter(
    (c: { campo1?: string; campo2?: string; resultado?: string }) => c.campo1 || c.campo2 || c.resultado,
  ) as readonly { campo1?: string; campo2?: string; resultado?: string }[];
  if (comparativos.length > 0) {
    linhas +=
      `<p style="margin:0 0 8px;"><strong>Etapa 7 | Comparativo das Estações:</strong></p><ul>` +
      comparativos.map((c) => `<li>${c.campo1 || '-'} × ${c.campo2 || '-'} = ${c.resultado || '-'}</li>`).join('') +
      '</ul>';
  }
  // Etapa 8: as famílias de cor marcadas.
  for (const familia of FAMILIAS_CORES_COLORACAO) {
    const valores = listaDe(dados[familia.campo]);
    if (valores.length > 0) linhas += `<p style="margin:0 0 8px;"><strong>${familia.titulo}:</strong> ${valores.join(', ')}</p>`;
  }
  // Etapa 9 e o resultado final.
  if (dados.estacao_definida) linhas += `<p style="margin:0 0 8px;"><strong>Etapa 9 | Estação Definida:</strong> ${textoDe(dados.estacao_definida)}</p>`;
  if (dados.resultado_final) linhas += `<p style="margin:0 0 8px;"><strong>Resultado Final:</strong> ${textoDe(dados.resultado_final)}</p>`;
  return linhas || '<p><em>Ficha preenchida, mas todos os campos vieram vazios.</em></p>';
}

/**
 * O radar como SVG em texto, para virar imagem no documento (`construirSvgRadar`, `4935-4976`).
 *
 * AS CORES SÃO VARIÁVEIS CSS, como no legado (`var(--border)`, `var(--dark)`): numa imagem isolada
 * elas não existem, e o legado as manda assim mesmo. O efeito provável é o radar sair sem traço e
 * com preenchimento preto no PNG; a suíte da A3 mede isso (defeito reproduzido, não corrigido).
 */
export function svgDoRadar(pontos: readonly PontoRaiz[]): string {
  const g = geometriaDoRadar(pontos);
  // Tamanho fixo e `xmlns` explícito, para a marcação funcionar como imagem isolada (`4957-4959`).
  let svg = `<svg viewBox="0 0 300 300" width="300" height="300" xmlns="http://www.w3.org/2000/svg">`;
  // As cinco grades; só a última é contínua. O espaço antes de `/>` é o do legado (`4965`).
  g.grades.forEach((pontosGrade, indice) => {
    const ultima = indice === g.grades.length - 1;
    svg += `<polygon points="${pontosGrade}" fill="none" stroke="var(--border)" stroke-width="1" ${ultima ? '' : 'stroke-dasharray="3,3"'}/>`;
  });
  // Os eixos, do centro (150, 150) à borda.
  for (const eixo of g.eixos) {
    svg += `<line x1="150" y1="150" x2="${eixo.x}" y2="${eixo.y}" stroke="var(--border)" stroke-width="1"/>`;
  }
  // O polígono das pontuações.
  svg += `<polygon points="${g.dados}" fill="var(--dark)" fill-opacity="0.16" stroke="var(--dark)" stroke-width="2"/>`;
  // Os rótulos dos estilos.
  for (const rotulo of g.rotulos) {
    svg += `<text x="${rotulo.x}" y="${rotulo.y}" font-size="10" font-family="Archivo, sans-serif" fill="var(--dark)" text-anchor="middle">${rotulo.texto}</text>`;
  }
  svg += `</svg>`;
  return svg;
}

/** O que o documento recebe. */
export interface EntradaDoDossie {
  readonly cliente: { readonly nome_completo: string | null; readonly email: string | null; readonly telefone: string | null };
  readonly geral: LinhaLida;
  readonly personalidade: LinhaLida;
  readonly pontosRaiz: readonly PontoRaiz[];
  readonly atendimento: LinhaLida;
  readonly milhoes: LinhaLida;
  readonly avaliativo: LinhaLida;
  readonly coloracao: LinhaLida;
  readonly abasHabilitadas: ReadonlySet<string>;
  readonly imagemRadarDataUri: string | null;
  // O instante da geração, já escrito (`new Date().toLocaleString('pt-BR')`, `5503`): vem de fora
  // para a regra não ler o relógio e poder ser provada com um valor fixo.
  readonly dataGeracao: string;
}

/** O HTML do `.doc` (`montarHtmlDossie`, `admin.html:5502-5557`). */
export function montarHtmlDossie(e: EntradaDoDossie): string {
  const tabelaRadar =
    '<table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse;">' +
    '<tr><th>Estilo Raiz</th><th>Pontuação</th></tr>' +
    e.pontosRaiz.map((p) => `<tr><td>${p.estilo}</td><td>${p.pontuacao}</td></tr>`).join('') +
    '</table>';
  const imagemRadar = e.imagemRadarDataUri
    ? `<img src="${e.imagemRadarDataUri}" width="320" height="320" alt="Radar de Estilo Raiz" style="display:block; margin:10px auto;" />`
    : '';
  const nome = e.cliente.nome_completo || '';
  // As marcas de abertura e fechamento do documento vêm partidas, com o mesmo cuidado do legado
  // (`5512-5515`): ferramenta que injeta código procurando o fechamento da página não confunde
  // este texto com o fechamento real.
  const abreDoc =
    '<' +
    'html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">' +
    '<' +
    'head><meta charset="UTF-8"><title>Dossiê · ' +
    nome +
    '</title></' +
    'head>' +
    '<' +
    'body style="font-family:Calibri, Arial, sans-serif;">';
  const fechaDoc = '</' + 'body>' + '</' + 'html>';
  // Cada seção só entra se a aba dela estiver vinculada a algum produto do lead (`5521-5546`).
  const secoes: string[] = [];
  if (e.abasHabilitadas.has('questionario')) secoes.push(`<h2>Questionário Geral</h2>${linhasRespostasHtml(e.geral, PERGUNTAS_GERAL)}`);
  if (e.abasHabilitadas.has('personalidade')) {
    secoes.push(`<h2>Questionário de Personalidade</h2>${linhasRespostasHtml(e.personalidade, PERGUNTAS_PERSONALIDADE)}`);
  }
  if (e.abasHabilitadas.has('radar_estilo_raiz')) secoes.push(`<h2>Radar de Estilo Raiz</h2>${imagemRadar}${tabelaRadar}`);
  if (e.abasHabilitadas.has('questionario_avaliativo')) {
    secoes.push(`<h2>Questionário Avaliativo</h2>${linhasRespostasHtml(e.avaliativo, PERGUNTAS_AVALIATIVO)}`);
  }
  if (e.abasHabilitadas.has('checklist_atendimento')) {
    secoes.push(`<h2>Checklist de Atendimento</h2>${linhasRespostasHtml(e.atendimento, PERGUNTAS_ATENDIMENTO)}`);
  }
  if (e.abasHabilitadas.has('checklist_milhoes')) secoes.push(`<h2>Checklist de Milhões: itens marcados</h2>${linhasMilhoesHtml(e.milhoes)}`);
  if (e.abasHabilitadas.has('coloracao')) secoes.push(`<h2>Coloração - Ficha de Atendimento</h2>${linhasColoracaoHtml(e.coloracao)}`);
  // O corpo com os espaços e as quebras exatos do modelo do legado (`5548-5554`).
  const corpo =
    '\n  <h1>Dossiê · ' +
    nome +
    '</h1>\n  <p>' +
    (e.cliente.email || '') +
    ' ' +
    (e.cliente.telefone ? '· ' + e.cliente.telefone : '') +
    '</p>\n  <p><em>Prévia gerada em ' +
    e.dataGeracao +
    '</em></p>\n\n  ' +
    secoes.join('\n\n  ') +
    '\n';
  return abreDoc + corpo + fechaDoc;
}
